import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";

import HttpManager from "../../../services/HttpManager";
import { copiarEnlace, copiarTexto, enlacePublico, Espacio, Nodo, Usuario } from "../../../services/v2/wappdrive.service";
import VisorImagen from "./visorImagen";
import WappIcono from "../../../components/wappIcono";
import { puedeDescargar } from "../../../utils/entregarArchivo";
import { PaginasPdf, esTelefono } from "../../../components/enrollment/visorArchivo";

export const errorTexto = (e: any): string => e?.response?.data?.message || e?.message || "Algo falló. Intenta otra vez.";
export const peso = (b: number): string => (b >= 1e9 ? (b / 1e9).toFixed(1) + " GB" : b >= 1e6 ? (b / 1e6).toFixed(1) + " MB" : b >= 1e3 ? Math.round(b / 1e3) + " KB" : b + " B");
export const fecha = (s: string | null): string => {
	if (!s) return "";
	const d = new Date(s.includes("T") || s.endsWith("Z") ? s : s.replace(" ", "T") + "Z");
	const hoy = new Date();
	const hora = d.toLocaleTimeString("es-MX", { hour: "numeric", minute: "2-digit", hour12: true }).replace(/p\.\s?m\./, "pm").replace(/a\.\s?m\./, "am");
	if (d.toDateString() === hoy.toDateString()) return "Hoy, " + hora;
	return d.toLocaleDateString("es-MX", { day: "numeric", month: "short", year: d.getFullYear() === hoy.getFullYear() ? undefined : "numeric" }).replace(".", "");
};

const http = () => HttpManager.getInstance().wappdriveService;
const tit = (t: string) => <Modal.Title style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 800 }}>{t}</Modal.Title>;

export const DlgNombre: React.FC<{ titulo: string; inicial: string; boton: string; onOk: (v: string) => Promise<void>; onClose: () => void }> = ({ titulo, inicial, boton, onOk, onClose }) => {
	const [v, setV] = useState(inicial);
	const [err, setErr] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const ok = async () => { setBusy(true); setErr(null); try { await onOk(v.trim()); } catch (e) { setErr(errorTexto(e)); setBusy(false); } };
	return (
		<Modal show onHide={onClose} backdropClassName="wd-modal-fondo" className="wd-modal">
			<Modal.Header closeButton>{tit(titulo)}</Modal.Header>
			<Modal.Body>
				<input className="wd-input" autoFocus value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && v.trim()) ok(); }}
					onFocus={(e) => { const p = e.target.value.lastIndexOf("."); e.target.setSelectionRange(0, p > 0 ? p : e.target.value.length); }} />
				{err ? <div className="wd-aviso">{err}</div> : null}
			</Modal.Body>
			<Modal.Footer><button className="wd-btn" onClick={onClose}>Cancelar</button><button className="wd-btn pri" disabled={!v.trim() || busy} onClick={ok}>{boton}</button></Modal.Footer>
		</Modal>
	);
};

/** Compartir (como Google Docs): personas con acceso, acceso general con enlace y "Copiar enlace". */
export const DlgCompartir: React.FC<{ campusId: number; nodo: Nodo; onClose: () => void }> = ({ campusId, nodo, onClose }) => {
	const [lista, setLista] = useState<{ user_id: number; name: string; email: string; permiso: "ver" | "editar" }[]>([]);
	// Acceso general como Google Docs: quién entra con el enlace (nadie más / Washington Academy / cualquiera) y con qué rol.
	const [audiencia, setAudiencia] = useState<"restringido" | "org" | "publico">("restringido");
	const [rol, setRol] = useState<"ver" | "editar">("ver");
	const [token, setToken] = useState<string | null>(null);
	// Fuera de Washington Academy: solo Super Admin y personas autorizadas, y siempre con motivo (queda en la bitácora).
	const [puedeExterno, setPuedeExterno] = useState(false);
	const [pubAntes, setPubAntes] = useState<"no" | "ver" | "editar">("no");
	const [motivo, setMotivo] = useState("");
	const [puede, setPuede] = useState(false);
	const [esCarpeta, setEsCarpeta] = useState<boolean>(!!nodo.is_folder);
	const [q, setQ] = useState("");
	const [res, setRes] = useState<Usuario[]>([]);
	const [err, setErr] = useState<string | null>(null);
	const [copiado, setCopiado] = useState(false);
	const [cambios, setCambios] = useState(false);
	useEffect(() => { http().compartidosDe(campusId, nodo.id).then((r) => { setLista(r.data.compartido as any); setPuede(r.data.puede); setEsCarpeta(r.data.is_folder); setToken(r.data.token_publico);
		const pub = r.data.publico || "no", gen = r.data.acceso_general || "restringido";
		setPuedeExterno(!!r.data.puede_externo); setPubAntes(pub);
		if (pub !== "no") { setAudiencia("publico"); setRol(pub); } else if (gen !== "restringido") { setAudiencia("org"); setRol(gen); } else { setAudiencia("restringido"); setRol("ver"); } }).catch((e) => setErr(errorTexto(e))); }, [nodo.id]);
	useEffect(() => { if (q.trim().length < 2) { setRes([]); return; } const t = setTimeout(() => http().usuarios(campusId, q).then((r) => setRes(r.data.usuarios)), 250); return () => clearTimeout(t); }, [q]);
	const guardar = async (cerrar: boolean) => {
		setErr(null);
		try {
			const r = await http().compartir(campusId, nodo.id, lista.map((x) => ({ user_id: x.user_id, permiso: x.permiso })),
				audiencia === "restringido" ? "restringido" : rol, esCarpeta ? undefined : audiencia === "publico" ? rol : "no", pideMotivo ? motivo.trim() : undefined);
			setCambios(false); setToken(r.data.token_publico); setPubAntes(audiencia === "publico" ? rol : "no"); setMotivo("");
			if (cerrar) onClose();
			return r.data.token_publico;
		} catch (e) { setErr(errorTexto(e)); return null; }
	};
	const copiar = async () => {
		let t = token;
		if (cambios && puede) t = await guardar(false);
		if (audiencia === "publico" && t) await copiarTexto(enlacePublico(t));
		else await copiarEnlace({ id: nodo.id, is_folder: esCarpeta, editor: nodo.editor });
		setCopiado(true); setTimeout(() => setCopiado(false), 2500);
	};
	const pideMotivo = audiencia === "publico" && pubAntes !== rol;
	const faltaMotivo = pideMotivo && motivo.trim().length < 10;
	const textoGeneral = audiencia === "restringido" ? "Solo las personas con acceso pueden abrirlo con el enlace."
		: (audiencia === "org" ? "Cualquier persona de Washington Academy con el enlace puede " : "Cualquier persona en internet con el enlace puede ") + (rol === "editar" ? "editar." : "verlo.");
	const inicial = (n: string) => n.split(" ").filter(Boolean).slice(0, 2).map((x) => x[0]).join("").toUpperCase();
	return (
		<Modal show onHide={onClose} backdropClassName="wd-modal-fondo" className="wd-modal wd-compartir-modal">
			<Modal.Header closeButton>{tit("Compartir “" + nodo.name.replace(/\.(docx|xlsx|pptx|pdf)$/i, "") + "”")}</Modal.Header>
			<Modal.Body>
				{puede ? (
					<div className="wd-compartir-buscar">
						<input className="wd-input" placeholder="Agregar personas por nombre o correo" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
						{res.length ? (
							<div className="wd-sugerencias">
								{res.filter((u) => !lista.some((x) => x.user_id === u.id)).map((u) => (
									<button key={u.id} onClick={() => { setLista([...lista, { user_id: u.id, name: u.name, email: u.email, permiso: "editar" }]); setQ(""); setCambios(true); }}>{u.name}<small>{u.email}</small></button>
								))}
							</div>
						) : null}
					</div>
				) : null}
				<div className="wd-sub">Personas con acceso</div>
				<div className="wd-compartidos">
					{lista.map((x) => (
						<div key={x.user_id} className="wd-compartido">
							<span className="wd-avatar">{inicial(x.name)}</span>
							<span><b>{x.name}</b><small>{x.email}</small></span>
							<select disabled={!puede} value={x.permiso} onChange={(e) => { setLista(lista.map((y) => (y.user_id === x.user_id ? { ...y, permiso: e.target.value as any } : y))); setCambios(true); }}>
								<option value="ver">Lector</option><option value="editar">Editor</option>
							</select>
							{puede ? <button className="wd-x" onClick={() => { setLista(lista.filter((y) => y.user_id !== x.user_id)); setCambios(true); }} aria-label="Quitar"><WappIcono n="x" /></button> : null}
						</div>
					))}
					{!lista.length ? <div className="wd-tenue">Además de quienes tienen acceso a la carpeta, nadie más en particular.</div> : null}
				</div>
				<div className="wd-sub">Acceso general</div>
				<div className="wd-general">
					<span className={"wd-general-ico" + (audiencia !== "restringido" ? " abierto" : "")}><WappIcono n={audiencia === "restringido" ? "lock" : audiencia === "org" ? "users" : "world"} /></span>
					<div>
						<select disabled={!puede} value={audiencia} onChange={(e) => { setAudiencia(e.target.value as any); setCambios(true); }}>
							<option value="restringido">Restringido</option>
							<option value="org">Washington Academy</option>
							{!esCarpeta && (puedeExterno || audiencia === "publico") ? <option value="publico">Cualquier persona con el enlace</option> : null}
						</select>
						<small>{textoGeneral}</small>
					</div>
					{audiencia !== "restringido" ? (
						<select disabled={!puede} value={rol} onChange={(e) => { setRol(e.target.value as any); setCambios(true); }}><option value="ver">Lector</option><option value="editar">Editor</option></select>
					) : null}
				</div>
				{pideMotivo ? (
					<div className="wd-motivo">
						<label htmlFor="wd-motivo"><WappIcono n="world" /> ¿Por qué lo compartes fuera de Washington Academy?</label>
						<textarea id="wd-motivo" className="wd-input" rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ej.: contrato para firma del proveedor de uniformes" />
						<small>Queda en la bitácora con tu nombre, la fecha y cada vez que alguien abra el enlace.</small>
					</div>
				) : null}
				{!puedeExterno && !esCarpeta ? <div className="wd-tenue wd-solo-org">Solo se puede compartir dentro de Washington Academy.</div> : null}
				{err ? <div className="wd-aviso">{err}</div> : null}
			</Modal.Body>
			<Modal.Footer className="wd-compartir-pie">
				<button className="wd-btn wd-copiar" onClick={copiar} disabled={faltaMotivo} title={faltaMotivo ? "Primero escribe el motivo" : undefined}><WappIcono n={copiado ? "check" : "link"} /> {copiado ? "Enlace copiado" : "Copiar enlace"}</button>
				<span style={{ flex: 1 }} />
				<button className="wd-btn pri" disabled={faltaMotivo} title={faltaMotivo ? "Primero escribe el motivo" : undefined} onClick={() => (puede && cambios ? guardar(true) : onClose())}>Listo</button>
			</Modal.Footer>
		</Modal>
	);
};

/** Mover a: elegir carpeta de equipo o Mis archivos y navegar hasta la carpeta destino. */
export const DlgMover: React.FC<{ campusId: number; espacios: Espacio[]; nodos: Nodo[]; onOk: () => void; onClose: () => void }> = ({ campusId, espacios, nodos, onOk, onClose }) => {
	const editables = espacios.filter((e) => e.permiso !== "ver");
	const [space, setSpace] = useState<number>(nodos[0]?.space_id || editables[0]?.id);
	const [parent, setParent] = useState<number | null>(null);
	const [migas, setMigas] = useState<{ id: number; name: string }[]>([]);
	const [carpetas, setCarpetas] = useState<Nodo[]>([]);
	const [err, setErr] = useState<string | null>(null);
	useEffect(() => {
		http().carpeta(campusId, space, parent, "nombre", 1, true).then((r) => { setCarpetas(r.data.items.filter((n) => n.is_folder && !nodos.some((x) => x.id === n.id))); setMigas(r.data.migas); }).catch((e) => setErr(errorTexto(e)));
	}, [space, parent]);
	const mover = async () => {
		setErr(null);
		try { for (const n of nodos) await http().actualizar(campusId, n.id, { space_id: space, parent_id: parent }); onOk(); } catch (e) { setErr(errorTexto(e)); }
	};
	return (
		<Modal show onHide={onClose} backdropClassName="wd-modal-fondo" className="wd-modal">
			<Modal.Header closeButton>{tit(nodos.length > 1 ? "Mover " + nodos.length + " elementos" : "Mover “" + nodos[0]?.name + "”")}</Modal.Header>
			<Modal.Body>
				<select className="wd-input" value={space} onChange={(e) => { setSpace(Number(e.target.value)); setParent(null); }}>
					{editables.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
				</select>
				<div className="wd-migas chico">
					<button onClick={() => setParent(null)}>{editables.find((e) => e.id === space)?.name}</button>
					{migas.map((m) => <React.Fragment key={m.id}><span>/</span><button onClick={() => setParent(m.id)}>{m.name}</button></React.Fragment>)}
				</div>
				<div className="wd-picker">
					{carpetas.map((c) => <button key={c.id} onClick={() => setParent(c.id)}><WappIcono n="folder" /> {c.name}</button>)}
					{!carpetas.length ? <div className="wd-tenue">Sin subcarpetas. Se moverá aquí.</div> : null}
				</div>
				{err ? <div className="wd-aviso">{err}</div> : null}
			</Modal.Body>
			<Modal.Footer><button className="wd-btn" onClick={onClose}>Cancelar</button><button className="wd-btn pri" onClick={mover}>Mover aquí</button></Modal.Footer>
		</Modal>
	);
};

export const DlgVersiones: React.FC<{ campusId: number; nodo: Nodo; onOk: () => void; onClose: () => void }> = ({ campusId, nodo, onOk, onClose }) => {
	const [d, setD] = useState<any>(null);
	const [err, setErr] = useState<string | null>(null);
	useEffect(() => { http().versiones(campusId, nodo.id).then((r) => setD(r.data)).catch((e) => setErr(errorTexto(e))); }, [nodo.id]);
	const [puedeBajar, setPuedeBajar] = useState<boolean>(false);
	useEffect(() => { puedeDescargar().then(setPuedeBajar); }, []);
	const bajar = async (v: number) => { try { window.location.href = (await http().urlVersion(campusId, nodo.id, v)).data.url; } catch (e) { setErr(errorTexto(e)); } };
	const restaurar = async (v: number) => {
		if (!window.confirm("¿Volver a esta versión? La actual queda guardada en el historial.")) return;
		try { await http().restaurarVersion(campusId, nodo.id, v); onOk(); } catch (e) { setErr(errorTexto(e)); }
	};
	return (
		<Modal show onHide={onClose} backdropClassName="wd-modal-fondo" className="wd-modal">
			<Modal.Header closeButton>{tit("Versiones de “" + nodo.name + "”")}</Modal.Header>
			<Modal.Body>
				{d ? (
					<div className="wd-versiones">
						<div className="wd-version actual"><b>Versión {d.actual.version} · actual</b><span>{fecha(d.actual.updated_at)} · {d.actual.autor || "—"} · {peso(d.actual.size)}</span></div>
						{d.anteriores.map((v: any) => (
							<div key={v.id} className="wd-version">
								<span><b>Versión {v.version}</b><small>{fecha(v.created_at)} · {v.autor || "—"} · {peso(v.size)}</small></span>
								<span className="wd-acc">{puedeBajar ? <button className="wd-btn chico" onClick={() => bajar(v.id)}>Descargar</button> : null}{nodo.permiso !== "ver" ? <button className="wd-btn chico" onClick={() => restaurar(v.id)}>Restaurar</button> : null}</span>
							</div>
						))}
						{!d.anteriores.length ? <div className="wd-tenue">No hay versiones anteriores.</div> : null}
					</div>
				) : <div className="wd-tenue">Cargando…</div>}
				{err ? <div className="wd-aviso">{err}</div> : null}
			</Modal.Body>
		</Modal>
	);
};

/** ¿Es foto? (para el visor de fotos y para pasar entre las fotos de la carpeta). */
export const esFoto = (n: Nodo) => !n.is_folder && ((n.mime || "").startsWith("image/") || ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg", "heic", "heif", "avif"].includes((n.ext || "").toLowerCase()));

/** Vista previa: fotos (con su visor: zoom, girar, ← →), PDF, video, audio y texto. */
export const DlgVista: React.FC<{ nodo: Nodo; url: string; onDescargar?: () => void; onClose: () => void; onAnterior?: () => void; onSiguiente?: () => void; posicion?: string }> =
	({ nodo, url, onDescargar, onClose, onAnterior, onSiguiente, posicion }) => {
	const m = nodo.mime || "", e = (nodo.ext || "").toLowerCase();
	const img = esFoto(nodo);
	const vid = m.startsWith("video/") || ["mp4", "mov", "webm", "m4v"].includes(e);
	const aud = m.startsWith("audio/") || ["mp3", "wav", "m4a", "ogg", "aac"].includes(e);
	const pdf = e === "pdf" || m === "application/pdf";
	// Phones and iPads can't page through a PDF inside an iframe (Safari shows only page 1): same pdf.js pages as the general viewer.
	// If pdf.js fails, back to the iframe (Esteban, 2026-10-22).
	const [pdfSinPaginas, setPdfSinPaginas] = useState<boolean>(false);
	const pdfFallo = useCallback(() => setPdfSinPaginas(true), []);
	const pdfEnPaginas = pdf && esTelefono() && !pdfSinPaginas;
	// Escape cierra la vista previa (Esteban, 2026-10-09). En fase de captura para que un video o PDF con el foco no se lo coma.
	useEffect(() => {
		const tecla = (ev: KeyboardEvent) => { if (ev.key === "Escape") { ev.stopPropagation(); onClose(); } };
		window.addEventListener("keydown", tecla, true);
		return () => window.removeEventListener("keydown", tecla, true);
	}, [onClose]);
	return (
		<div className="wd-vista" onClick={onClose} role="dialog" aria-modal="true" aria-label={"Vista previa de " + nodo.name}>
			<div className="wd-vista-cab" onClick={(x) => x.stopPropagation()}>
				<b>{nodo.name}</b><span className="wd-tenue">{peso(nodo.size)}</span>
				{onDescargar ? <button className="wd-btn" onClick={onDescargar}>Descargar</button> : null}<button className="wd-x grande" onClick={onClose} aria-label="Cerrar"><WappIcono n="x" /></button>
			</div>
			<div className="wd-vista-cuerpo" onClick={(x) => x.stopPropagation()}>
				{img ? <VisorImagen url={url} nombre={nodo.name} onAnterior={onAnterior} onSiguiente={onSiguiente} posicion={posicion} /> : vid ? <video src={url} controls autoPlay controlsList="nodownload" onContextMenu={(x) => x.preventDefault()} /> : aud ? <audio controlsList="nodownload" src={url} controls autoPlay /> : pdfEnPaginas ? <PaginasPdf key={url} url={url} onFallo={pdfFallo} /> : pdf ? <iframe src={onDescargar ? url : url + "#toolbar=0"} title={nodo.name} />
					: <div className="wd-sinvista">Este tipo de archivo no tiene vista previa.{onDescargar ? <><br /><button className="wd-btn pri" onClick={onDescargar}>Descargar</button></> : null}</div>}
			</div>
		</div>
	);
};

/** Carpeta de equipo: nombre y quién entra (personas o roles, con permiso). */
export const DlgEspacio: React.FC<{ campusId: number; id: number | null; onOk: () => void; onClose: () => void }> = ({ campusId, id, onOk, onClose }) => {
	const [d, setD] = useState<any>({ espacio: { name: "", descripcion: "" }, miembros: [], roles: [], planteles: [], puede: true });
	const [q, setQ] = useState("");
	const [res, setRes] = useState<Usuario[]>([]);
	const [err, setErr] = useState<string | null>(null);
	useEffect(() => { http().espacio(campusId, id || 0).then((r) => setD(r.data)).catch((e) => setErr(errorTexto(e))); }, [id]);
	useEffect(() => { if (q.trim().length < 2) { setRes([]); return; } const t = setTimeout(() => http().usuarios(campusId, q).then((r) => setRes(r.data.usuarios)), 250); return () => clearTimeout(t); }, [q]);
	const admins: string[] = d.miembros.filter((m: any) => m.permiso === "admin" && m.user_id && m.name).map((m: any) => m.name);
	const set = (k: string, v: any) => setD({ ...d, espacio: { ...d.espacio, [k]: v } });
	const setM = (i: number, k: string, v: any) => setD({ ...d, miembros: d.miembros.map((m: any, j: number) => (j === i ? { ...m, [k]: v } : m)) });
	const guardar = async () => {
		setErr(null);
		try { await http().guardarEspacio(campusId, id, { name: d.espacio.name, descripcion: d.espacio.descripcion, miembros: d.miembros }); onOk(); } catch (e) { setErr(errorTexto(e)); }
	};
	return (
		<Modal show onHide={onClose} size="lg" backdropClassName="wd-modal-fondo" className="wd-modal">
			<Modal.Header closeButton>{tit(id ? (d.espacio.name ? "Compartir “" + d.espacio.name + "”" : "Compartir") : "Nueva carpeta de equipo")}</Modal.Header>
			<Modal.Body>
				<label className="wd-label">Nombre</label>
				<input className="wd-input" value={d.espacio.name || ""} disabled={!d.puede} onChange={(e) => set("name", e.target.value)} placeholder="Ventas Honduras" />
				<label className="wd-label">Descripción</label>
				<input className="wd-input" value={d.espacio.descripcion || ""} disabled={!d.puede} onChange={(e) => set("descripcion", e.target.value)} />
				<label className="wd-label">Quién entra</label>
				{d.miembros.map((m: any, i: number) => (
					<div key={i} className="wd-miembro">
						<span>{m.user_id ? <><b>{m.name}</b><small>{m.email}</small></> : <><b>Rol: {d.roles.find((r: any) => r.id === Number(m.role_id))?.name || m.rol}</b><small>{m.campus_id ? "solo " + (d.planteles.find((p: any) => p.id === Number(m.campus_id))?.name || "") : "todos los planteles"}</small></>}</span>
						{!m.user_id ? (
							<select disabled={!d.puede} value={m.campus_id || ""} onChange={(e) => setM(i, "campus_id", e.target.value ? Number(e.target.value) : null)}>
								<option value="">Todos los planteles</option>{d.planteles.map((p: any) => <option key={p.id} value={p.id}>{p.name.replace("Washington Academy ", "")}</option>)}
							</select>
						) : null}
						<select disabled={!d.puede} value={m.permiso} onChange={(e) => setM(i, "permiso", e.target.value)}>
							<option value="ver">Ver</option><option value="editar">Editar</option><option value="admin">Administrar</option>
						</select>
						{d.puede ? <button className="wd-x" onClick={() => setD({ ...d, miembros: d.miembros.filter((_: any, j: number) => j !== i) })}>×</button> : null}
					</div>
				))}
				{d.puede ? (
					<div className="wd-agregar">
						<input className="wd-input" placeholder="Agregar persona…" value={q} onChange={(e) => setQ(e.target.value)} />
						<select className="wd-input" value="" onChange={(e) => { if (e.target.value) setD({ ...d, miembros: [...d.miembros, { role_id: Number(e.target.value), permiso: "ver" }] }); }}>
							<option value="">Agregar un rol completo…</option>{d.roles.map((r: any) => <option key={r.id} value={r.id}>{r.name}</option>)}
						</select>
					</div>
				) : null}
				{res.length ? (
					<div className="wd-sugerencias">
						{res.filter((u) => !d.miembros.some((m: any) => m.user_id === u.id)).map((u) => (
							<button key={u.id} onClick={() => { setD({ ...d, miembros: [...d.miembros, { user_id: u.id, name: u.name, email: u.email, permiso: "editar" }] }); setQ(""); }}>{u.name}<small>{u.email}</small></button>
						))}
					</div>
				) : null}
				{id && !d.puede && !err ? (
					<div className="wd-aviso">
						Solo quien administra esta carpeta puede darle acceso a alguien
						{admins.length ? <>: <b>{admins.join(", ")}</b>.</> : "."}
					</div>
				) : null}
				{err ? <div className="wd-aviso">{err}</div> : null}
			</Modal.Body>
			<Modal.Footer><button className="wd-btn" onClick={onClose}>Cerrar</button>{d.puede ? <button className="wd-btn pri" disabled={!d.espacio.name} onClick={guardar}>Guardar</button> : null}</Modal.Footer>
		</Modal>
	);
};
